// "Get exact terms": the application dialog (borrower or introducer,
// introducer log in / register, the call-first tickbox), the submission,
// and the thank-you step shown in the same dialog afterwards, so it works on
// whichever site the engine sits in. Shared by every finance type: each
// supplies an ApplyAdapter for what differs (the chosen product's summary,
// extra notes for the adviser, checks). Ported from bridgingeye.co.uk's
// homepage and thank-you page.
import { trackOnce } from './funnel';
import type { Parts } from './dom';
import { applyMoneyInputFormatting, escapeHtml, money, parseMoneyInput } from './format';
import type { PublicBrand } from './types';
import { attachCompanyAutocomplete, type CompanyResult } from './widgets/companies';
import { attachDialCode, phoneFieldValue } from './widgets/dial';
import { attachPlaces, setAddressValue } from './widgets/places';
import { attachPropertyFacts } from './widgets/propertyFacts';
import { attachManualAddress } from './widgets/manualAddress';
import { portfolioCommentBlock, type PortfolioProperty } from './bridging/comments';

/** The application's own answers, and the quote it applies to. */
export interface ApplyState {
  quoteId: string | null;
  /** The chosen result's code; null for "let an adviser find a product". */
  selectedCode: string | null;
  who: string;
  introMode: 'login' | 'register' | null;
  introCompanyAddress: string;
  address: string;
  comments: string;
}

/** What a finance type tells the application dialog. */
export interface ApplyAdapter {
  financeType: string;
  state: ApplyState;
  /** "case" or "scheme", as in "once a broker has checked the case". */
  caseNoun: string;
  /** How the timescale answer is labelled in the adviser's notes ("Completion needed"). */
  timescaleNote: string;
  /** The chosen product, for the card at the top of the dialog; null if none. */
  selectedProduct(code: string | null): { lenderName: string; summary: string } | null;
  /** Company borrower: ask for the company name. */
  showCompanyField(): boolean;
  /** Regulated bridging doesn't take free-text comments. */
  showComments(): boolean;
  /** The customer's answers the adviser needs that aren't matched on (refurb figures etc.). */
  extraCommentBlocks(portfolioProperties: () => PortfolioProperty[]): string[];
  /** '' when the answers can be sent; otherwise what to fix. */
  validationError(): string;
  /** Mixed portfolio (bridging): more than one security property. */
  portfolio?: { isPortfolio(): boolean; propertyValueGbp(): number; propertyTypeLabel(): string };
  /** Runs a fresh quote, so a second application gets its own. */
  refetch(): Promise<void>;
}

interface ApplicationSummary {
  name: string;
  firstName: string;
  email: string;
  phone: string;
  companyName: string | null;
  loanAmountGbp: number | null;
  referenceNumber: string | null;
  introduced: boolean;
  callIntroducerFirst: boolean;
  introducerEmailed: boolean;
  factfindAvailable: boolean;
}

declare global {
  interface Window { gtag?: (...args: unknown[]) => void }
}

/** Google's "G", for "Continue with Google". */
const GOOGLE_G = '<svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>';

const INTRODUCER_TITLES: Record<string, string> = {
  broker: 'Broker details',
  estate_agent: 'Estate agent details',
  banker_lender: 'Banker / lender details',
  wealth_manager: 'Wealth manager details',
  other_introducer: 'Introducer details',
};

export function setupApply(parts: Parts, brand: PublicBrand, adapter: ApplyAdapter): { openApplyDialog(): void } {
  const { state } = adapter;
  const $ = <T extends HTMLElement = HTMLElement>(id: string) => parts.el<T>(id);
  const input = (id: string) => $<HTMLInputElement>(id);
  const val = (id: string) => input(id).value.trim();

  attachDialCode(input('input-phone-dial'), input('input-phone'));
  attachDialCode(input('input-intro-phone-dial'), input('input-intro-phone'));
  input('input-address').addEventListener('input', (e) => { state.address = (e.target as HTMLInputElement).value; });
  input('input-comments').addEventListener('input', (e) => { state.comments = (e.target as HTMLInputElement).value; });
  // "From public records" under the address: when one is picked from the suggestions, or typed and left.
  const factsBox = parts.maybe('property-facts');
  const showFacts = factsBox ? attachPropertyFacts(factsBox) : () => undefined;
  input('input-address').addEventListener('change', (e) => showFacts((e.target as HTMLInputElement).value));
  void attachPlaces(input('input-address'), (value) => { state.address = value; showFacts(value); }, true);
  attachManualAddress(input('input-address'), (value) => { state.address = value; showFacts(value); }, true);

  // "Submit my pre-application" only fits a borrower applying themselves.
  const isBorrower = () => !state.who || state.who === 'borrower';
  const submitButtonLabel = () => (isBorrower() ? 'Submit my pre-application' : 'Submit to adviser');
  // "Asking for a friend" is treated like a borrower: no introducer details.
  const isIntroducer = () => !!state.who && state.who !== 'borrower' && state.who !== 'asking_for_a_friend';

  function updateIntroducerPanel() {
    const wrap = $('popdown-wrap');
    const introducer = isIntroducer();
    wrap.classList.toggle('open', introducer);
    // Phone and email are always the borrower's; say so when someone else is filling in.
    $('phone-info-tip').hidden = isBorrower();
    $('email-info-tip').hidden = isBorrower();
    $('submit-btn').textContent = submitButtonLabel();
    // Introduced case: the introducer's details first, then the client's
    // under a "Client details" heading with the call-first option.
    const clientSection = $('client-details-section');
    const commentsField = $('comments-field');
    if (introducer) {
      if (wrap.nextElementSibling !== clientSection) clientSection.parentNode?.insertBefore(wrap, clientSection);
    } else if (commentsField.nextElementSibling !== wrap) {
      commentsField.parentNode?.insertBefore(wrap, commentsField.nextElementSibling);
    }
    clientSection.classList.toggle('is-introduced', !isBorrower());
    const clientTitle = $('client-details-title');
    clientTitle.classList.toggle('hidden', isBorrower());
    clientTitle.textContent = state.who === 'asking_for_a_friend' ? 'Borrower details' : 'Client details';
    $('call-first-field').classList.toggle('hidden', !introducer);
    if (!introducer) input('input-call-first').checked = false;
    $('introducer-fca-field').classList.toggle('hidden', state.who !== 'broker');
    $('introducer-panel-title').textContent = INTRODUCER_TITLES[state.who] || 'Introducer details';
    if (introducer) {
      void checkIntroducerSession();
      void attachPlaces(input('input-intro-company-address'), (value) => { state.introCompanyAddress = value; }, false);
      attachManualAddress(input('input-intro-company-address'), (value) => { state.introCompanyAddress = value; }, false);
    }
  }

  // ---- Introducer: log in (details come from their account) or register ----
  // Registering never shows whether the email is already an introducer: the
  // server emails either a "confirm and set a password" link or "this case
  // was added to your account" (routes/quote.ts).
  let introVerified: { name: string; email: string; company: string } | null = null;
  let introSessionChecked = false;
  function setIntroMode(mode: 'login' | 'register' | null) {
    state.introMode = mode;
    parts.all<HTMLButtonElement>('#intro-mode [data-intro-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.introMode === mode)));
    renderIntroAccount();
  }
  function renderIntroAccount() {
    const verified = !!introVerified;
    $('intro-mode').classList.toggle('hidden', verified);
    $('intro-hint').classList.toggle('hidden', verified);
    $('intro-logged-in').classList.toggle('hidden', !verified);
    $('intro-login').classList.toggle('hidden', verified || state.introMode !== 'login');
    $('intro-register').classList.toggle('hidden', verified || state.introMode !== 'register');
    if (introVerified) {
      $('intro-logged-name').textContent = introVerified.name;
      $('intro-logged-company').textContent = introVerified.company ? ', ' + introVerified.company : '';
      $('intro-logged-email').textContent = introVerified.email;
    }
  }
  async function loadIntroducerProfile() {
    const res = await fetch('/portal/api/profile');
    if (!res.ok) throw new Error('profile');
    const p = await res.json() as Record<string, string | null>;
    introVerified = { name: [p.firstName, p.surname].filter(Boolean).join(' '), email: p.email || '', company: p.companyName || '' };
    // Keep the hidden register fields in step; the server uses the account's saved details anyway.
    const set = (id: string, v: string | null | undefined) => { input(id).value = v || ''; };
    set('input-intro-first-name', p.firstName); set('input-intro-surname', p.surname); set('input-intro-email', p.email);
    set('input-intro-company-name', p.companyName); set('input-intro-company-address', p.companyAddress); set('input-intro-fca-number', p.fcaNumber);
    set('input-intro-phone', (p.phone || '').replace(/^\+44/, ''));
    renderIntroAccount();
  }
  async function checkIntroducerSession() {
    if (introSessionChecked) return;
    introSessionChecked = true;
    try {
      const res = await fetch('/portal/api/me');
      if (!res.ok) return;
      const me = await res.json() as { portalUserType?: string };
      if (me.portalUserType === 'introducer') await loadIntroducerProfile();
    } catch { /* not logged in */ }
  }
  $('intro-mode').addEventListener('click', (e) => {
    const b = (e.target as Element).closest<HTMLButtonElement>('[data-intro-mode]');
    if (!b) return;
    const mode = b.dataset.introMode as 'login' | 'register';
    setIntroMode(mode);
    input(mode === 'login' ? 'intro-login-email' : 'input-intro-first-name').focus();
  });
  $('intro-login-btn').addEventListener('click', async () => {
    const errEl = $('intro-login-error');
    const btn = $<HTMLButtonElement>('intro-login-btn');
    errEl.classList.add('hidden');
    const email = val('intro-login-email');
    const password = input('intro-login-password').value;
    if (!email || !password) { errEl.textContent = 'Please enter your email address and password.'; errEl.classList.remove('hidden'); return; }
    btn.disabled = true;
    try {
      const res = await fetch('/portal/api/login', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, password, preferredType: 'introducer' }) });
      const data = await res.json().catch(() => ({})) as { portalUserType?: string };
      if (res.status === 429) throw new Error('Too many attempts - please try again in a few minutes.');
      if (!res.ok) throw new Error('That email and password didn’t match an account.');
      if (data.portalUserType !== 'introducer') {
        // Their password opened their client account: switch to the introducer view if they have one.
        const me = await (await fetch('/portal/api/me')).json() as { otherAccount?: string };
        if (me.otherAccount === 'introducer') await fetch('/portal/api/switch', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
        else throw new Error("That login is for a client account, not an introducer account - choose “I'm new - register” instead.");
      }
      input('intro-login-password').value = '';
      await loadIntroducerProfile();
    } catch (err) {
      errEl.textContent = (err as Error).message === 'profile' ? 'Logged in, but we couldn’t load your details - please try again.' : (err as Error).message;
      errEl.classList.remove('hidden');
    } finally {
      btn.disabled = false;
    }
  });
  $('intro-forgot-btn').addEventListener('click', async () => {
    const email = val('intro-login-email');
    const errEl = $('intro-login-error');
    if (!email) { errEl.textContent = 'Enter your email address first.'; errEl.classList.remove('hidden'); return; }
    errEl.classList.add('hidden');
    await fetch('/portal/api/forgot-password', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, for: 'introducer' }) }).catch(() => undefined);
    $('intro-forgot-msg').classList.remove('hidden');
  });
  $('intro-logout-btn').addEventListener('click', async () => {
    await fetch('/portal/api/logout', { method: 'POST' }).catch(() => undefined);
    introVerified = null;
    ['input-intro-first-name', 'input-intro-surname', 'input-intro-email', 'input-intro-phone', 'input-intro-company-name', 'input-intro-company-address', 'input-intro-fca-number'].forEach((id) => { input(id).value = ''; });
    setIntroMode(null);
  });
  $<HTMLSelectElement>('input-who').addEventListener('change', (e) => { state.who = (e.target as HTMLSelectElement).value; updateIntroducerPanel(); });

  attachCompanyAutocomplete(input('input-intro-company-name'), (r: CompanyResult) => {
    input('input-intro-registration-number').value = r.companyNumber;
    if (r.address) setAddressValue(input('input-intro-company-address'), r.address, (value) => { state.introCompanyAddress = value; }, false);
  });

  // Company borrower: Companies House lookup, with the company's SIC codes shown.
  attachCompanyAutocomplete(input('input-company-name'), (r: CompanyResult) => {
    const panel = $('company-sic-codes');
    panel.innerHTML = r.sicCodes && r.sicCodes.length > 0
      ? `<p class="text-muted" style="font-size:12px;margin:6px 0 2px">SIC codes:</p><ul style="margin:0;padding-left:18px;font-size:12px">${r.sicCodes.map((s) => `<li>${escapeHtml(s)}</li>`).join('')}</ul>`
      : '<p class="text-muted" style="font-size:12px;margin:4px 0 0">No SIC codes on file for this company.</p>';
    panel.classList.remove('hidden');
  });
  input('input-company-name').addEventListener('input', (e) => {
    if ((e.target as HTMLInputElement).value.trim() === '') $('company-sic-codes').classList.add('hidden');
  });

  // ---------- Mixed portfolio: extra security properties ----------
  let portfolioRowSeq = 0;
  const isPortfolio = () => !!adapter.portfolio?.isPortfolio();
  function portfolioProperties(): PortfolioProperty[] {
    const valueInput = parts.maybe<HTMLInputElement>('input-address-value');
    const first = { address: val('input-address'), value: valueInput ? parseMoneyInput(valueInput.value) : 0 };
    const rest = Array.from(parts.maybe('portfolio-rows')?.children ?? []).map((li) => ({
      address: (li.querySelector('[data-portfolio-address]') as HTMLInputElement).value.trim(),
      value: parseMoneyInput((li.querySelector('[data-portfolio-value]') as HTMLInputElement).value),
    }));
    return [first, ...rest];
  }
  function updatePortfolioTotal() {
    if (!adapter.portfolio) return;
    $('portfolio-total').textContent = money(portfolioProperties().reduce((sum, p) => sum + p.value, 0));
    $('portfolio-estimate').textContent = money(adapter.portfolio.propertyValueGbp());
  }
  function renumberPortfolioRows() {
    Array.from($('portfolio-rows').children).forEach((li, i) => {
      const n = i + 2;
      (li.querySelector('label') as HTMLLabelElement).textContent = `Security property ${n}`;
      li.querySelector('[data-portfolio-value]')?.setAttribute('aria-label', `Value of security property ${n}`);
      li.querySelector('[data-portfolio-remove]')?.setAttribute('aria-label', `Remove security property ${n}`);
    });
  }
  function addPortfolioRow() {
    const id = `portfolio-address-${++portfolioRowSeq}`;
    const li = document.createElement('li');
    li.innerHTML = `
      <div class="portfolio-row-head"><label for="${id}"></label><button type="button" class="link-btn" data-portfolio-remove>Remove</button></div>
      <div class="address-row">
        <input class="control" type="text" id="${id}" data-portfolio-address placeholder="Start typing an address…" autocomplete="off" />
        <div class="affix-field address-value"><span class="affix" aria-hidden="true">£</span><input class="control" type="text" inputmode="numeric" autocomplete="off" data-portfolio-value placeholder="Value" /></div>
      </div>`;
    $('portfolio-rows').appendChild(li);
    li.querySelector<HTMLInputElement>('[data-portfolio-value]')?.addEventListener('input', (e) => { applyMoneyInputFormatting(e.target as HTMLInputElement); updatePortfolioTotal(); });
    li.querySelector('[data-portfolio-remove]')?.addEventListener('click', () => {
      li.remove();
      renumberPortfolioRows();
      updatePortfolioTotal();
      $('add-portfolio-property-btn').focus();
    });
    renumberPortfolioRows();
    const addressInput = li.querySelector('[data-portfolio-address]') as HTMLInputElement;
    void attachPlaces(addressInput, () => undefined, true);
    attachManualAddress(addressInput, () => undefined, true);
    addressInput.focus();
  }
  function updatePortfolioFields() {
    if (!adapter.portfolio) return;
    const portfolio = isPortfolio();
    $('portfolio-properties').classList.toggle('hidden', !portfolio);
    $('input-address-value-wrap').classList.toggle('hidden', !portfolio);
    $('input-address-label').textContent = portfolio ? 'Security property 1' : 'Security property address';
    updatePortfolioTotal();
  }
  if (adapter.portfolio) {
    $('add-portfolio-property-btn').addEventListener('click', addPortfolioRow);
    input('input-address-value').addEventListener('input', (e) => { applyMoneyInputFormatting(e.target as HTMLInputElement); updatePortfolioTotal(); });
  }

  // ---------- The dialog ----------
  const backdrop = $('apply-dialog-backdrop');
  let returnFocusEl: HTMLElement | null = null;
  function openApplyDialog() {
    showStep('apply');
    const sel = adapter.selectedProduct(state.selectedCode);
    $('company-name-field').classList.toggle('hidden', !adapter.showCompanyField());
    $('comments-field').classList.toggle('hidden', !adapter.showComments());
    updatePortfolioFields();
    const card = $('selected-product-card');
    if (sel) {
      card.classList.remove('hidden');
      $('selected-product-code').textContent = sel.lenderName;
      $('selected-product-summary').textContent = sel.summary;
      $('apply-line').textContent = `We'll confirm exact terms with ${sel.lenderName || 'your chosen lender'} as soon as a broker has checked the ${adapter.caseNoun}.`;
    } else {
      card.classList.add('hidden');
      $('apply-line').textContent = 'Leave your details and a broker will be in touch to take things further.';
    }
    trackOnce('apply_open', { finance_type: adapter.financeType, product_chosen: !!sel });
    returnFocusEl = (parts.activeElement() as HTMLElement | null) || (document.activeElement as HTMLElement | null);
    backdrop.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    $('apply-dialog-close').focus();
  }
  function closeApplyDialog() {
    backdrop.classList.add('hidden');
    document.body.style.overflow = '';
    returnFocusEl?.focus?.();
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !backdrop.classList.contains('hidden')) closeApplyDialog();
  });
  $('apply-dialog-close').addEventListener('click', closeApplyDialog);
  backdrop.addEventListener('click', (e) => { if (e.target === e.currentTarget) closeApplyDialog(); });
  function showStep(step: 'apply' | 'thanks') {
    $('apply-step').classList.toggle('hidden', step !== 'apply');
    $('thanks-step').classList.toggle('hidden', step !== 'thanks');
    // Start each step at the top (the form is long, the thank-you short).
    const dialog = backdrop.querySelector('.dialog');
    if (dialog) dialog.scrollTop = 0;
  }

  // ---------- Submit ----------
  // Drop-off tracking (funnel.ts): the first field typed in the form.
  $<HTMLFormElement>('wizard-form').addEventListener('input', () => trackOnce('apply_form_start', { finance_type: adapter.financeType }));
  $<HTMLFormElement>('wizard-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = $('apply-error');
    const fail = (message: string) => { errEl.textContent = message; errEl.classList.remove('hidden'); };
    errEl.classList.add('hidden');

    const title = $<HTMLSelectElement>('input-title').value;
    const firstName = val('input-first-name');
    const surname = val('input-surname');
    // "First Surname": the thank-you step and the admin take the first word as the first name.
    const name = `${firstName} ${surname}`.trim();
    const phone = phoneFieldValue(input('input-phone'));
    const email = val('input-email');
    const timescale = $<HTMLSelectElement>('input-timescale').value;
    if (!title || !firstName || !surname || !phone || !email) return fail('Please fill in your title, first name, surname, phone and email.');
    if (!timescale) return fail('Please tell us when you need to complete.');
    const answersProblem = adapter.validationError();
    if (answersProblem) return fail(answersProblem);
    if (!input('input-consent-data-sharing').checked) return fail('Please agree to the privacy policy and data sharing consent to continue.');

    const applicantRole = state.who || 'borrower';
    const requiresIntroducerDetails = isIntroducer();
    const introFirstName = val('input-intro-first-name');
    const introSurname = val('input-intro-surname');
    const introEmail = val('input-intro-email');
    const introPhone = phoneFieldValue(input('input-intro-phone'));
    const introCompanyName = val('input-intro-company-name');
    if (requiresIntroducerDetails && !introVerified && state.introMode !== 'register') return fail('Please log in to your introducer account, or choose “I\'m new - register”.');
    if (requiresIntroducerDetails && !introVerified && (!introFirstName || !introSurname || !introEmail || !introPhone || !introCompanyName)) {
      return fail('Please fill in your first name, surname, email, phone and company name.');
    }

    const submitBtn = $<HTMLButtonElement>('submit-btn');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting…';
    const quoteId = state.quoteId as string;
    try {
      const res = await fetch('/api/quote/apply', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          quoteId,
          // null: "Let an adviser find a product".
          productCode: state.selectedCode,
          name, phone, email,
          applicantRole,
          callIntroducerFirst: requiresIntroducerDetails && input('input-call-first').checked,
          consentMarketing: input('input-consent').checked,
          consentDataSharing: input('input-consent-data-sharing').checked,
          propertyAddress: state.address,
          additionalComments: [
            `Title: ${title}\n${adapter.timescaleNote}: ${timescale}`,
            ...adapter.extraCommentBlocks(portfolioProperties),
            isPortfolio() && adapter.portfolio ? portfolioCommentBlock(adapter.portfolio.propertyTypeLabel(), portfolioProperties(), adapter.portfolio.propertyValueGbp()) : '',
            adapter.showComments() ? state.comments : '',
          ].filter(Boolean).join('\n\n'),
          companyName: val('input-company-name'),
          introducerFirstName: introFirstName,
          introducerSurname: introSurname,
          introducerEmail: introEmail,
          introducerPhone: introPhone,
          introducerCompanyName: introCompanyName,
          introducerCompanyAddress: val('input-intro-company-address'),
          introducerFcaNumber: val('input-intro-fca-number'),
          introducerRegistrationNumber: val('input-intro-registration-number'),
        }),
      });
      const data = await res.json() as { errors?: { field: string; message: string }[]; error?: string };
      if (!res.ok) throw new Error((data.errors || []).map((e2) => e2.field + ': ' + e2.message).join('; ') || data.error || 'Request failed');
      window.gtag?.('event', 'generate_lead', { finance_type: adapter.financeType });
      // The thank-you step used to be its own page (thank-you.html); report it
      // as a page view too, so Analytics conversions set up on that page still count.
      window.gtag?.('event', 'page_view', { page_title: 'Thank you', page_location: `${window.location.origin}/thank-you.html`, page_path: '/thank-you.html' });
      await showThanks(quoteId);
      // The next application (if any) gets a quote of its own.
      void adapter.refetch();
    } catch (err) {
      fail((err as Error).message);
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = submitButtonLabel();
    }
  });

  // ---------- Thank-you step ----------
  async function showThanks(quoteId: string) {
    const el = $('thanks-step');
    const tel = escapeHtml(brand.phone.replace(/\s/g, ''));
    let data: ApplicationSummary;
    try {
      const res = await fetch('/api/quote/' + encodeURIComponent(quoteId) + '/summary');
      if (!res.ok) throw new Error('not found');
      data = await res.json();
    } catch {
      el.innerHTML = `<h3 class="dialog-title">Thank you.</h3><p>Your details have been sent to one of our advisers, who will be in touch shortly. You can also call us on <a href="tel:${tel}">${escapeHtml(brand.phone)}</a>.</p>`;
      showStep('thanks');
      return;
    }
    const portalLanding = () => (data.factfindAvailable ? `${brand.portalUrl}?startFactfind=${encodeURIComponent(quoteId)}` : brand.portalUrl);
    const summary = `
      <dl class="thanks-summary">
        <div><dt>Case number</dt><dd>${escapeHtml(data.referenceNumber || '—')}</dd></div>
        <div><dt>${data.introduced ? 'Client name' : 'Applicant name'}</dt><dd>${escapeHtml(data.name)}</dd></div>
        ${data.companyName ? `<div><dt>Company name</dt><dd>${escapeHtml(data.companyName)}</dd></div>` : ''}
        <div><dt>Loan amount</dt><dd>${data.loanAmountGbp === null ? '—' : money(data.loanAmountGbp)}</dd></div>
      </dl>
      <div class="call-box">If you'd like to speak to an adviser now, call <a class="phone" href="tel:${tel}">${escapeHtml(brand.phone)}</a> and quote case number <strong>${escapeHtml(data.referenceNumber || '—')}</strong>.</div>`;

    if (data.introduced) {
      // The reader is the introducer, and the details on file are the
      // client's: no fact find link and no account set-up (that would email the client).
      let next = data.callIntroducerFirst
        ? "An adviser will call you before contacting your client - we haven't sent your client anything. We'll keep you updated at every stage."
        : 'An adviser will be in touch with you and your client shortly, and we will keep you updated at every stage.';
      if (data.introducerEmailed) next += ' You can track this case in your introducer portal - if you’re new to us, check your email to set up your account.';
      el.innerHTML = `
        <p class="eyebrow">Case received</p>
        <h3 class="dialog-title">Thank you.</h3>
        <p>Your client's case has been sent to one of our advisers.</p>
        ${summary}
        <div class="account-box"><h4>What happens next</h4><p>${escapeHtml(next)}</p></div>
        <button type="button" class="btn btn-outline" data-thanks-close>Close</button>`;
      el.querySelector('[data-thanks-close]')?.addEventListener('click', closeApplyDialog);
      showStep('thanks');
      return;
    }

    el.innerHTML = `
      <p class="eyebrow">Pre-application received</p>
      <h3 class="dialog-title">Thank you, ${escapeHtml(data.firstName)}.</h3>
      <p>Your quotation has been sent to one of our advisers, who will be in contact with you shortly.</p>
      ${summary}
      <div class="account-box">
        <h4>Speed things up</h4>
        <p>${data.factfindAvailable
          ? "<strong>Start your factfind now.</strong> You can begin completing a factfind now, so that your adviser will have as much detail as possible when they assess your case. You can save it and come back at any time."
          : 'If you would like to speed up the process for achieving indicative terms, please create an account below and complete your profile.'}</p>
        <a id="thanks-factfind-link" class="btn btn-primary hidden" href="${escapeHtml(portalLanding())}">Start my factfind →</a>
        <div id="thanks-google" class="account-form hidden">
          <a class="google-btn" href="${escapeHtml(`${brand.portalUrl}api/google/start?quote=${encodeURIComponent(quoteId)}`)}">${GOOGLE_G}Continue with Google</a>
          <p class="text-muted" style="font-size:14px">Use the Google account for ${escapeHtml(data.email)}. No password needed.</p>
          <p class="or-divider">or choose a password</p>
        </div>
        <form id="thanks-create-form" class="account-form">
          <div class="field"><label for="thanks-create-password">Choose a password (min. 12 characters)</label><input class="control" type="password" id="thanks-create-password" minlength="12" autocomplete="new-password" required /></div>
          <button class="btn btn-primary" type="submit">${data.factfindAvailable ? 'Create account &amp; start my factfind' : 'Create account'}</button>
          <p class="text-muted" style="font-size:14px">Already have an account? <button type="button" class="link-btn" id="thanks-show-login">Log in</button></p>
        </form>
        <form id="thanks-login-form" class="account-form hidden">
          <div class="field"><label for="thanks-login-password">Password</label><input class="control" type="password" id="thanks-login-password" autocomplete="current-password" required /></div>
          <button class="btn btn-primary" type="submit">${data.factfindAvailable ? 'Log in &amp; start my factfind' : 'Log in'}</button>
          <p><button type="button" class="link-btn" id="thanks-forgot">Forgot your password?</button></p>
        </form>
        <p class="form-error hidden" id="thanks-error" role="alert"></p>
        <p class="success hidden" id="thanks-message" role="status"></p>
      </div>
      <button type="button" class="btn btn-outline" data-thanks-close>Close</button>`;
    showStep('thanks');

    const q = <T extends HTMLElement>(sel: string) => el.querySelector(sel) as T;
    const errEl = q<HTMLElement>('#thanks-error');
    const msgEl = q<HTMLElement>('#thanks-message');
    const createForm = q<HTMLFormElement>('#thanks-create-form');
    const loginForm = q<HTMLFormElement>('#thanks-login-form');
    const fail = (m: string) => { errEl.textContent = m; errEl.classList.remove('hidden'); };
    el.querySelector('[data-thanks-close]')?.addEventListener('click', closeApplyDialog);
    q<HTMLButtonElement>('#thanks-show-login').addEventListener('click', () => {
      createForm.classList.add('hidden');
      loginForm.classList.remove('hidden');
      q<HTMLInputElement>('#thanks-login-password').focus();
    });

    // "Continue with Google" (src/portalGoogleSignIn.ts), when it's set up: Google's address must be data.email.
    const googleBox = q<HTMLElement>('#thanks-google');
    void fetch('/portal/api/google/available').then((r) => r.json()).then((g: { available?: boolean }) => {
      if (g.available) googleBox.classList.remove('hidden');
    }).catch(() => undefined);
    q<HTMLButtonElement>('#thanks-show-login').addEventListener('click', () => { googleBox.querySelector('.or-divider')!.textContent = 'or log in with your password'; });

    // Already logged in as this person: straight to the fact find.
    if (data.factfindAvailable) {
      try {
        const meRes = await fetch('/portal/api/me');
        const me = meRes.ok ? await meRes.json() as { email?: string } : null;
        if (me?.email && me.email.toLowerCase() === data.email.toLowerCase()) {
          createForm.classList.add('hidden');
          googleBox.remove();
          q<HTMLElement>('#thanks-factfind-link').classList.remove('hidden');
        }
      } catch { /* not logged in */ }
    }

    async function logIn(password: string): Promise<boolean> {
      const res = await fetch('/portal/api/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: data.email, password, preferredType: 'client' }),
      });
      return res.ok;
    }
    loginForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      errEl.classList.add('hidden');
      if (await logIn(q<HTMLInputElement>('#thanks-login-password').value)) window.location.href = portalLanding();
      else fail('Incorrect password — please try again.');
    });
    q<HTMLButtonElement>('#thanks-forgot').addEventListener('click', async () => {
      await fetch('/portal/api/forgot-password', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email: data.email }) }).catch(() => undefined);
      msgEl.textContent = "If that email has an account, we've sent a reset link.";
      msgEl.classList.remove('hidden');
    });
    createForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      errEl.classList.add('hidden');
      const password = q<HTMLInputElement>('#thanks-create-password').value;
      try {
        const res = await fetch('/api/quote/create-portal-account', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ name: data.name, email: data.email, phone: data.phone, password, referenceNumber: data.referenceNumber, quoteId }),
        });
        const result = await res.json() as { status?: string; message?: string; error?: string };
        if (!res.ok) throw new Error(result.message || result.error || 'Could not create your account');
        if (result.status === 'check_email') {
          // Same for everyone: the email says whether they already had an account.
          createForm.classList.add('hidden');
          googleBox.remove();
          msgEl.innerHTML = `<strong>Check your email.</strong> We've sent a link to ${escapeHtml(data.email)}. Click it to finish setting up your account - you'll be logged straight in. If you already have an account, the email explains how to log in.`;
          msgEl.classList.remove('hidden');
          return;
        }
        if (!(await logIn(password))) throw new Error('Account created — please log in.');
        window.location.href = portalLanding();
      } catch (err) {
        fail((err as Error).message);
      }
    });
  }

  updateIntroducerPanel();
  return { openApplyDialog };
}
